import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Shield, ShieldAlert, ShieldCheck, Clock } from 'lucide-react';

export default function PasswordHealthCard() {
  const { user } = useAuth();
  const pe = user?.password_expiry;
  if (!pe) return null;
  const days = Math.max(0, Math.floor(pe.days_until_expiry));
  const expired = pe.expired || pe.days_until_expiry <= 0;
  const warn = !expired && pe.days_until_expiry <= 3;
  const fresh = !expired && !warn;

  const tone = expired ? {
    bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-900', icon: 'text-red-600',
    Icon: ShieldAlert, title: 'Password expired',
    note: 'Please set a new password to continue using the HRMS.',
  } : warn ? {
    bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-900', icon: 'text-amber-600',
    Icon: ShieldAlert, title: `${days} day${days !== 1 ? 's' : ''} until your password expires`,
    note: 'Set a new one now so you\'re never locked out.',
  } : {
    bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-900', icon: 'text-emerald-600',
    Icon: ShieldCheck, title: 'Password is healthy',
    note: `Next change in ${days} day${days !== 1 ? 's' : ''} · refresh every 15 days.`,
  };

  const Icon = tone.Icon;

  // Progress bar fill (capped 0–100%)
  const pct = Math.min(100, Math.max(0, (pe.days_until_expiry / (pe.max_age_days || 15)) * 100));

  return (
    <div className={`${tone.bg} ${tone.border} border rounded-2xl p-5 flex items-start gap-4`} data-testid="password-health-card">
      <div className={`w-11 h-11 rounded-xl bg-white flex items-center justify-center flex-shrink-0 ${tone.icon}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className={`font-heading font-semibold ${tone.text}`}>{tone.title}</div>
          {fresh && (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-emerald-700 bg-white border border-emerald-200 rounded-full px-2 py-0.5">
              <Clock className="w-2.5 h-2.5" /> 15-day rotation
            </span>
          )}
        </div>
        <div className={`text-sm mt-0.5 ${tone.text} opacity-80`}>{tone.note}</div>
        <div className="mt-3 h-1.5 w-full bg-white/60 rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all ${
            expired ? 'bg-red-500' : warn ? 'bg-amber-500' : 'bg-emerald-500'
          }`} style={{ width: `${pct}%` }} data-testid="password-health-bar" />
        </div>
        <Link to="/change-password" data-testid="password-health-cta" className="inline-block mt-3 text-xs font-bold uppercase tracking-wider text-[#3D4A8E] hover:underline">
          {expired ? 'Set new password →' : 'Change password →'}
        </Link>
      </div>
    </div>
  );
}
